/** Inbox, requests and approvals (S60 to S64), C-8 decisions. docs/50 §4.6, §4.7, docs/52 §3.7, docs/31. */
import { z } from 'zod';
import { AppPath, Cursor, IsoDateTime, Limit, text } from '../common.ts';
import { DecisionState, ProposalState, RequestChannel, RequestRating, RequestStatus } from '../enums.ts';
import { DecisionId, LocationId, ProposalId, RequestId, RequestMessageId, UploadId } from '../ids.ts';
import { Attachments, PromisedBy } from './shared.ts';

export const QuickRequest = z.enum(['change_hours', 'add_service', 'fix_site', 'billing_question', 'something_else']);

/** What the client sees: four steps, never the nine internal types (docs/52 S62). */
export const ClientRequestStage = z.enum(['received', 'working', 'ready_for_ok', 'live', 'closed']);

export const RequestSummary = z.object({
  id: RequestId,
  number: z.number().int().positive(),
  subject: z.string().max(160).nullable(),
  stage: ClientRequestStage,
  status: RequestStatus,
  waitingOnYou: z.boolean(),
  promisedBy: z.iso.date().nullable(),
  promisedLabel: z.string().max(60).nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  locationId: LocationId.nullable(),
});

export const RequestListQuery = z
  .object({
    group: z.enum(['waiting_on_you', 'in_progress', 'done']).optional(),
    location: LocationId.optional(),
    cursor: Cursor.optional(),
    limit: Limit.optional(),
  })
  .strict();
export const RequestList = z.object({ items: z.array(RequestSummary), nextCursor: Cursor.nullable() });

export const RequestCreateBody = z
  .object({
    body: text(5000),
    quick: QuickRequest.optional(),
    attachments: Attachments.optional(),
    voiceUploadId: UploadId.optional(),
    locationId: LocationId.optional(),
    idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  })
  .strict();

export const RequestMessageView = z.object({
  id: RequestMessageId,
  author: z.object({ kind: z.enum(['you', 'teammate', 'us', 'system']), name: z.string().max(80) }),
  body: z.string().max(5000),
  attachments: z.array(z.object({ name: z.string().max(200), url: AppPath })),
  createdAt: IsoDateTime,
});

export const ProposalView = z.object({
  id: ProposalId,
  state: ProposalState,
  /** Readable change list: "Hours on Saturday: 8 am to 2 pm, was closed" (docs/52 S62). */
  changes: z.array(z.object({ label: z.string().max(160), before: z.string().max(500).nullable(), after: z.string().max(500).nullable() })),
  screenshots: z.array(z.object({ width: z.union([z.literal(390), z.literal(1280)]), before: AppPath, after: AppPath })),
  previewUrl: z.string().max(2048).nullable(),
  needsYourOk: z.boolean(),
  googleChanges: z.array(z.string().max(160)),
});

export const RequestDetail = RequestSummary.extend({
  channel: RequestChannel,
  timeline: z.array(z.object({ stage: ClientRequestStage, at: IsoDateTime.nullable() })),
  messages: z.array(RequestMessageView),
  proposal: ProposalView.nullable(),
  canApprove: z.boolean(),
  /** "Put it back" on anything approved in the last 30 days (docs/50 §4.6). */
  rollbackUntil: IsoDateTime.nullable(),
  rating: RequestRating.nullable(),
});

export const RequestCreated = z.object({ request: RequestDetail }).extend(PromisedBy.shape);

export const RequestMessageBody = z.object({ body: text(5000), attachments: Attachments.optional() }).strict();
export const RequestApproveBody = z.object({ proposalId: ProposalId }).strict();
export const RequestChangesBody = z.object({ proposalId: ProposalId, note: text(2000) }).strict();
export const RequestRollbackBody = z.object({ reason: text(500).optional() }).strict();
export const RequestRatingBody = z.object({ rating: RequestRating }).strict();

// ---------------------------------------------------------------------------------------------- inbox

export const InboxItem = z.object({
  kind: z.enum(['request', 'approval', 'decision', 'message', 'report']),
  id: z.string().max(64),
  title: z.string().max(160),
  link: AppPath,
  promisedLabel: z.string().max(60).nullable(),
  at: IsoDateTime,
});
export const Inbox = z.object({
  waitingOnYou: z.array(InboxItem),
  inProgress: z.array(InboxItem),
  done: z.array(InboxItem),
});

// ---------------------------------------------------------------------------------------------- decisions (C-8)

export const DecisionCard = z.object({
  id: DecisionId,
  locationId: LocationId,
  field: z.string().max(80),
  title: z.string().max(160),
  change: z.object({ from: z.string().max(300).nullable(), to: z.string().max(300) }),
  why: z.string().max(600),
  risks: z.array(z.string().max(300)).min(1),
  whatWeWillDo: z.string().max(600),
  state: DecisionState,
  version: z.string().max(40),
  canApprove: z.boolean(),
  createdAt: IsoDateTime,
  decidedAt: IsoDateTime.nullable(),
});

/** Typed-name approval against the exact version shown (docs/52 S64). */
export const DecisionApproveBody = z.object({ typedName: text(80, 2), version: z.string().max(40) }).strict();
export const DecisionDeclineBody = z.object({ choice: z.enum(['not_now', 'talk_first']), note: text(1000).optional() }).strict();
